import {
	Box,
	Card,
	Table,
	TableBody,
	TableCell,
	TableRow,
	Typography,
} from "@mui/material";
import type { RollingTotalData } from "../models/MillOverview2";

type Props = {
	data: RollingTotalData;
};

const formatNumber = (
	value: number | null | undefined,
	decimals = 0,
): string => {
	if (value === undefined || value === null) return "-";
	return Number(value.toFixed(decimals)).toLocaleString("en-US", {
		minimumFractionDigits: 0,
		maximumFractionDigits: decimals,
	});
};

const formatPushTime = (value: number | null | undefined): string => {
	if (value == null) return "-";
	return `${value.toLocaleString("en-US", {
		minimumFractionDigits: 1,
		maximumFractionDigits: 1,
	})} sec`;
};

export const RollingTotalStats = ({ data }: Props) => {
	const rows = [
		{
			label: "Billet Tons Charged",
			current: formatNumber(data.BilletTonsCharged),
			scheduled: formatNumber(data.BilletTonsChargedScheduled),
		},
		{
			label: "Billet Pc's. Charged",
			current: formatNumber(data.BilletPcsCharged),
			scheduled: formatNumber(data.BilletPcsChargedScheduled),
		},
		{
			label: "Rolled Tons",
			current: formatNumber(data.RolledTons),
			scheduled: formatNumber(data.RolledTonsScheduled),
		},
		{
			label: "Finished Tons",
			current: formatNumber(data.FinishedTons),
			scheduled: formatNumber(data.FinishedTonsScheduled),
		},
		{
			label: "Finished Bundles",
			current:
				data.FinishedBundles != null ? formatNumber(data.FinishedBundles) : "-",
			scheduled: "",
		},
		{
			label: "Mill Yield",
			current: `${formatNumber(data.MillYield, 1)}%`,
			scheduled: `${formatNumber(data.MillYieldScheduled, 1)}%`,
		},
		{
			label: "Finished Yield",
			current: `${formatNumber(data.FinishedYield, 1)}%`,
			scheduled: `${formatNumber(data.FinishedYieldScheduled, 1)}%`,
		},
	];

	const statsRows = [
		{
			label: "Total Cobbles",
			value: formatNumber(data.TotalCobbles),
		},
		{
			label: "Production",
			value: `${formatNumber(data.Production, 2)}%`,
			indicator: "production",
		},
		{
			label: "Delays",
			value: `${formatNumber(data.Delays, 2)}%`,
			indicator: "delay",
		},
		{
			label: "Hours - Actual",
			value: formatNumber(data.HoursActual, 2),
		},
		{
			label: "Hours - Schld.",
			value: formatNumber(data.HoursScheduled, 2),
		},
		{
			label: "TPH - Actual",
			value: formatNumber(data.TPHActual, 2),
		},
		{
			label: "TPH - Targeted",
			value: formatNumber(data.TPHTargeted, 2),
		},
	];

	return (
		<Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
			<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
				Rolling Total
			</Typography>

			<Box sx={{ display: "flex", flexDirection: "row", gap: 1, flexGrow: 1 }}>
				<Card sx={{ flex: 1, p: 1 }}>
					<Table
						size="small"
						sx={{ "& td, & th": { py: 0.25, px: 0.5, borderBottom: "none" } }}
					>
						<TableBody>
							<TableRow>
								<TableCell
									sx={{ fontWeight: 500, fontSize: "0.7rem" }}
								></TableCell>
								<TableCell
									align="right"
									sx={{ fontWeight: 600, fontSize: "0.65rem", color: "#666" }}
								>
									Actual/Schd.
								</TableCell>
							</TableRow>
							{rows.map((row) => (
								<TableRow key={row.label}>
									<TableCell sx={{ fontWeight: 500, fontSize: "0.7rem" }}>
										{row.label}
									</TableCell>
									<TableCell align="right" sx={{ fontSize: "0.7rem" }}>
										{row.current}
										{row.scheduled && (
											<span style={{ color: "#666" }}>/{row.scheduled}</span>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Card>

				<Card sx={{ flex: 1, p: 1 }}>
					<Table
						size="small"
						sx={{ "& td, & th": { py: 0.25, px: 0.5, borderBottom: "none" } }}
					>
						<TableBody>
							{statsRows.map((row) => (
								<TableRow key={row.label}>
									<TableCell sx={{ fontSize: "0.7rem" }}>
										{row.indicator && (
											<Box
												component="span"
												sx={{
													display: "inline-block",
													width: 8,
													height: 8,
													borderRadius: "50%",
													backgroundColor:
														row.indicator === "production"
															? "#4CAF50"
															: "#F44336",
													mr: 0.5,
													verticalAlign: "middle",
												}}
											/>
										)}
										{row.label}
									</TableCell>
									<TableCell align="right" sx={{ fontSize: "0.7rem" }}>
										{row.value}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Card>
			</Box>
		</Box>
	);
};
